"use client";

import { useClientTranslations } from "@/lib/translate";
import { useApplyJob } from "@/modules/hooks/apply/useApplyJob";
import { Job } from "@/modules/types/job/job";
import { useEffect, useState } from "react";
import ApplyCallLoginPopup from "./ApplyCallLoginPopup";
import CircularProgress from "../commons/CircularProgress";
import JobApplyStatusBadge from "./JobApplyStatusBadge";
import { SaveJobButton } from "./SaveJobButton";
import { MoneyUnit } from "@/modules/types/enum";
import ApplyJobModal from "./ApplyJobModal";

export const ApplyButtonGroup = ({ job }: { job: Job }) => {
  const { t } = useClientTranslations("job-info");
  const [isVisible, setIsVisible] = useState(false);
  const {
    loading: applyLoading,
    openLoginModal: applyOpenLoginModal,
    onCloseLoginModal: onCloseApplyLoginModal,
    openApplyJobModal,
    setOpenApplyJobModal,
    applyJob,
    onApplyJob,
    applyStatus,
  } = useApplyJob(job.id);

  useEffect(() => {
    const handleScroll = () => {
      const jobInfoElement = document.getElementById("job-info");

      if (jobInfoElement) {
        const rect = jobInfoElement.getBoundingClientRect();
        const elementTop = rect.top;
        const elementBottom = rect.bottom;

        if (elementTop <= 0 && elementBottom > 500) {
          setIsVisible(true);
        } else {
          setIsVisible(false);
        }
      }
    };

    // Initial check
    handleScroll();

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll);

    // Cleanup
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const jobApplyStatusComponent = () => {
    if (!job.jobApplyStatus && !applyStatus) return null;
    return (
      <JobApplyStatusBadge
        jobApplyId={job.jobApplyId || ""}
        status={job.jobApplyStatus || applyStatus!}
      />
    );
  };

  return (
    <div
      className={`w-full md:hidden gap-3 items-center fixed bottom-0 left-0 bg-white p-3 transition-all duration-300 ${
        isVisible ? "flex md:hidden" : "hidden"
      }`}
    >
      <ApplyCallLoginPopup
        applyOpenLoginModal={applyOpenLoginModal}
        onCloseApplyLoginModal={onCloseApplyLoginModal}
      />
      {job.isApplied || applyStatus ? (
        jobApplyStatusComponent()
      ) : (
        <button
          onClick={applyJob}
          className="bg-primary text-white py-3 max-h-fit rounded-lg hover:bg-primaryDark transition-colors duration-300 w-full"
        >
          {applyLoading ? <CircularProgress /> : t("applyNow")}
        </button>
      )}
      <SaveJobButton
        jobId={job.id}
        isSaved={job.isSaved}
        disabled={job.isApplied}
      />
      <ApplyJobModal
        open={openApplyJobModal}
        onClose={() => setOpenApplyJobModal(false)}
        onSubmit={async (
          coverLetter: string,
          min: number,
          max: number,
          unit: MoneyUnit
        ) => {
          await onApplyJob(coverLetter, min, max, unit);
          setOpenApplyJobModal(false);
        }}
        loading={applyLoading}
      />
    </div>
  );
};
